import { useState } from "react";
import { Coins, Receipt, CalendarDays, CalendarRange } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AdminTitle } from "@/admin/AdminLayout";
import { GradientStat } from "@/components/Colorful";
import { FeeTrendChart } from "@/admin/FeeTrendChart";
import { useApi } from "@/lib/api";
import { rp, fmtDate } from "@/lib/format";

const ymd = (d) => d.toLocaleDateString("en-CA", { timeZone: "Asia/Jakarta" });
const MONTHS = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
const fmtMonth = (p) => `${MONTHS[Number(p.slice(5, 7)) - 1]} ${p.slice(0, 4)}`;

export default function AdminFeeReport() {
  const now = new Date();
  const [from, setFrom] = useState(`${ymd(now).slice(0, 8)}01`);
  const [to, setTo] = useState(ymd(now));
  const [view, setView] = useState("daily");
  const days = from && to ? (new Date(to) - new Date(from)) / 86400000 : -1;
  const valid = days >= 0 && days <= 1100;
  const { data } = useApi(valid ? `/admin/reports/fees?date_from=${from}&date_to=${to}` : null);
  const rows = (valid && data?.[view]) || [];

  return (
    <div>
      <AdminTitle icon={Coins} title="Pendapatan Biaya Layanan" sub="Total Biaya Layanan Sistem dari transfer antar anggota">
        <div className="flex flex-wrap items-end gap-3">
          <div><label className="text-xs font-semibold text-slate-600">Dari</label><Input type="date" data-testid="fee-date-from" className="bg-white h-10 mt-1" value={from} max={to} onChange={(e) => setFrom(e.target.value)} /></div>
          <div><label className="text-xs font-semibold text-slate-600">Sampai</label><Input type="date" data-testid="fee-date-to" className="bg-white h-10 mt-1" value={to} min={from} onChange={(e) => setTo(e.target.value)} /></div>
        </div>
      </AdminTitle>
      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-5">
        <GradientStat tone="orange" icon={Coins} label="Total Pendapatan Periode" value={rp(valid ? data?.fee_total || 0 : 0)} testid="fee-total" />
        <GradientStat tone="blue" icon={Receipt} label="Jumlah Transfer Berbiaya" value={valid ? data?.count || 0 : 0} testid="fee-count" />
        <GradientStat tone="green" icon={CalendarDays} label="Hari Ini" value={rp(data?.today_fee || 0)} testid="fee-today" />
        <GradientStat tone="purple" icon={CalendarRange} label="Bulan Ini" value={rp(data?.month_fee || 0)} sub={`Biaya saat ini ${rp(data?.current_fee ?? 0)}/transfer`} testid="fee-month" />
      </div>
      <FeeTrendChart data={data?.trend} />
      <Tabs value={view} onValueChange={setView} className="mb-3">
        <TabsList><TabsTrigger value="daily" data-testid="fee-tab-daily">Per Hari</TabsTrigger><TabsTrigger value="monthly" data-testid="fee-tab-monthly">Per Bulan</TabsTrigger></TabsList>
      </Tabs>
      <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto shadow-sm">
        <table className="w-full text-sm" data-testid="fee-table">
          <thead className="bg-gradient-to-r from-slate-50 to-amber-50 text-left text-xs font-mono uppercase tracking-wide text-slate-600">
            <tr><th className="p-3">{view === "daily" ? "Tanggal" : "Bulan"}</th><th className="p-3 text-right">Jumlah Transfer</th><th className="p-3 text-right">Nilai Transfer</th><th className="p-3 text-right">Pendapatan Biaya</th></tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-slate-500" data-testid="fee-empty">{valid ? "Belum ada pendapatan biaya pada periode ini." : "Pilih rentang tanggal yang valid."}</td></tr>}
            {rows.map((r) => (
              <tr key={r.period} className="border-t border-slate-100 hover:bg-amber-50/40" data-testid={`fee-row-${r.period}`}>
                <td className="p-3 font-semibold">{view === "daily" ? fmtDate(r.period) : fmtMonth(r.period)}</td>
                <td className="p-3 text-right">{r.count}</td>
                <td className="p-3 text-right whitespace-nowrap">{rp(r.transfer_total)}</td>
                <td className="p-3 text-right font-bold text-amber-600 whitespace-nowrap">{rp(r.fee_total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
